import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useClaws } from "../hooks/useClaws";
import { useCachedQuery } from "../hooks/useCachedQuery";
import { getCsrfHeader } from "../lib/csrf";
import type { AiStatus, OpenClaw, ProviderKeyInfo } from "../types/openclaw";
import { ProviderKeyList } from "../components/ai-config/ProviderKeyList";
import { CapabilityList } from "../components/ai-config/CapabilityList";
import { RemoveKeyDialog } from "../components/ai-config/RemoveKeyDialog";
import { Skeleton } from "../components/dashboard/Skeleton";
import { PROVIDERS } from "../components/ai-config/constants";
import styles from "./AiModels.module.css";
import { AdminPageIntro } from "../components/admin/AdminPageIntro";

// Hoisted to module scope so the parent re-rendering on each keystroke
// does not unmount/remount the <input> (which would drop IME composition,
// reset cursor position, and close password-manager popups).
type OnboardKeyInputProps = {
  provider: typeof PROVIDERS[number];
  keyInput: string;
  onChange: (v: string) => void;
  submitting: boolean;
  onSubmit: (provider: string) => Promise<void> | void;
  onCancel: () => void;
};

const OnboardKeyInput = ({ provider, keyInput, onChange, submitting, onSubmit, onCancel }: OnboardKeyInputProps): JSX.Element => (
  <form className={styles.keyForm} onSubmit={(e: FormEvent) => { e.preventDefault(); void onSubmit(provider.id); }}>
    <input type="text" className={styles.keyInput}
      style={{ WebkitTextSecurity: "disc" } as React.CSSProperties}
      value={keyInput} onChange={(e) => onChange(e.target.value)}
      placeholder={provider.placeholder} autoFocus disabled={submitting}
      autoComplete="off" data-1p-ignore="true" data-lpignore="true" />
    <button type="submit" className={styles.primaryBtn} disabled={submitting || !keyInput.trim()}>
      {submitting ? "..." : "Save"}
    </button>
    <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={submitting}>Cancel</button>
  </form>
);

// Mirrors the toggle-row + key-list + capabilities layout so nothing
// reflows when the (LiteLLM-backed, slow) status call lands.
const AiStatusSkeleton = (): JSX.Element => (
  <div aria-hidden>
    <section className={styles.section}>
      <div className={styles.toggleRow}>
        <Skeleton width="72px" height="0.9rem" />
        <Skeleton width="52px" height="1.6rem" radius="999px" />
      </div>
    </section>
    <section className={styles.section}>
      <Skeleton width="80px" height="1rem" />
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className={styles.toggleRow}>
          <Skeleton width={`${[34, 26, 30][i]}%`} height="0.9rem" />
          <Skeleton width="64px" height="0.9rem" />
        </div>
      ))}
    </section>
    <section className={styles.section}>
      <Skeleton width="110px" height="1rem" />
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className={styles.toggleRow}>
          <Skeleton width={`${[28, 36, 24, 32][i]}%`} height="0.9rem" />
        </div>
      ))}
    </section>
  </div>
);

type AiModelsBodyProps = {
  claw: OpenClaw;
};

const AiModelsBody = ({ claw }: AiModelsBodyProps): JSX.Element => {
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  // First-time onboarding state
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const [addingProvider, setAddingProvider] = useState<string | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [showAltProviders, setShowAltProviders] = useState(false);

  const statusUrl = `/api/claws/${encodeURIComponent(claw.tenantId)}/ai`;
  const fetcher = useMemo(
    () => async (signal: AbortSignal): Promise<AiStatus> => {
      const res = await fetch(statusUrl, { credentials: "include", signal });
      if (!res.ok) throw new Error(`Failed to load AI status (${res.status})`);
      return (await res.json()) as AiStatus;
    },
    [statusUrl],
  );

  // No dwell interval: the status call fans out to LiteLLM upstream, so
  // freshness comes from the mount revalidate + post-mutation refetches.
  const { status, data: aiStatus, isFetching, refetch } = useCachedQuery<AiStatus>({
    cacheResource: `ai-status:${claw.tenantId}`,
    queryKey: statusUrl,
    fetcher,
    refreshIntervalMs: 0,
  });

  const addKey = async (provider: string, key: string): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/claws/${encodeURIComponent(claw.tenantId)}/ai/credentials`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ provider, key }),
      });
      if (res.ok) {
        await refetch();
      } else {
        const body = await res.json().catch(() => null);
        setError(body?.message ?? `Failed (${res.status})`);
      }
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    setSubmitting(false);
  };

  const removeKey = async (provider: string): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/claws/${encodeURIComponent(claw.tenantId)}/ai/credentials/${encodeURIComponent(provider)}`, {
        method: "DELETE", credentials: "include", headers: { ...getCsrfHeader() },
      });
      if (res.ok) await refetch();
    } catch { /* silent */ }
    setSubmitting(false);
  };

  const toggleAi = async (): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/claws/${encodeURIComponent(claw.tenantId)}/ai/toggle`, {
        method: "POST", credentials: "include", headers: { ...getCsrfHeader() },
      });
      if (res.status === 403 || res.status === 409) {
        setError("AI access has been paused by your administrator.");
        await refetch();
      } else if (res.ok) {
        await refetch();
      }
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    setSubmitting(false);
  };

  // First-time key submission (onboarding flow)
  const submitOnboardKey = async (provider: string): Promise<void> => {
    if (!keyInput.trim()) return;
    await addKey(provider, keyInput.trim());
    setKeyInput("");
    setEditingProvider(null);
    setAddingProvider(null);
  };

  const configuredKeys = aiStatus?.providers?.filter((p: ProviderKeyInfo) => p.configured) ?? [];
  const hasAnyKey = configuredKeys.length > 0;
  const isAdminDisabled = aiStatus?.aiEnabled === "admin_disabled";
  // Per-provider key registration only works in LiteLLM mode. openrouter-
  // mode tenants set their key via the legacy /llm-token admin path, so
  // we hide the registration UI here to avoid a 409 dead-end.
  const isLitellmMode = aiStatus?.aiMode === "litellm";

  const onboardKeyInputChange = (v: string): void => { setKeyInput(v); setError(null); };

  if (status === "loading") return <AiStatusSkeleton />;
  if (aiStatus === null) {
    return <div className={styles.error}>Couldn't load AI status — try reloading.</div>;
  }

  return (
    <div className={status === "ready" && isFetching ? styles.bodyFetching : undefined}>
      {isAdminDisabled && (
        <div className={styles.adminNotice}>AI access has been paused by your administrator.</div>
      )}

      {aiStatus.aiMode === "chatgpt" && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>ChatGPT subscription</h2>
          <p className={styles.sectionHint}>
            Manage the connected account from your instance dashboard. No OpenRouter key
            is required, and requests do not automatically fall back to OpenRouter.
          </p>
        </section>
      )}

      {/* First-time onboarding: no keys yet */}
      {!isAdminDisabled && !hasAnyKey && isLitellmMode && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Get Started</h2>
          <p className={styles.sectionHint}>Your OpenClaw needs an API key to power the AI assistant.</p>

          {editingProvider === "openrouter" ? (
            <>
              <p className={styles.providerLabel}>OpenRouter</p>
              <OnboardKeyInput
                provider={PROVIDERS[0]}
                keyInput={keyInput}
                onChange={onboardKeyInputChange}
                submitting={submitting}
                onSubmit={submitOnboardKey}
                onCancel={() => { setEditingProvider(null); setKeyInput(""); }}
              />
            </>
          ) : addingProvider ? (
            <>
              <p className={styles.providerLabel}>{PROVIDERS.find((p) => p.id === addingProvider)?.label}</p>
              <OnboardKeyInput
                provider={PROVIDERS.find((p) => p.id === addingProvider)!}
                keyInput={keyInput}
                onChange={onboardKeyInputChange}
                submitting={submitting}
                onSubmit={submitOnboardKey}
                onCancel={() => { setAddingProvider(null); setKeyInput(""); }}
              />
            </>
          ) : (
            <>
              <button type="button" className={styles.primaryBtn}
                onClick={() => { setEditingProvider("openrouter"); setKeyInput(""); setError(null); }}>
                Add OpenRouter Key
              </button>
              <p className={styles.providerHint}>
                <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">Get an OpenRouter key</a>
                {" "}&mdash; covers the most AI capabilities with a single key.
              </p>
              {!showAltProviders ? (
                <button type="button" className={styles.expandBtn} onClick={() => setShowAltProviders(true)}>
                  Use a different provider instead
                </button>
              ) : (
                <div className={styles.altProviders}>
                  <h3 className={styles.altProvidersTitle}>Other providers</h3>
                  {PROVIDERS.filter((p) => !p.primary).map((p) => (
                    <button key={p.id} type="button" className={styles.altProviderBtn}
                      onClick={() => { setAddingProvider(p.id); setKeyInput(""); setError(null); }}>
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Show capabilities even during onboarding */}
          <div style={{ marginTop: "1.5rem" }}>
            <h3 className={styles.sectionTitle}>Capabilities</h3>
            <CapabilityList
              capabilities={aiStatus.capabilities ?? []}
              configuredProviders={new Set((aiStatus.providers ?? []).filter((p) => p.configured).map((p) => p.provider))}
            />
          </div>
        </section>
      )}

      {/* Main view: has keys, OR mode doesn't surface per-provider keys here. */}
      {!isAdminDisabled && (hasAnyKey || !isLitellmMode) && (
        <>
          <section className={styles.section}>
            <div className={styles.toggleRow}>
              <span className={styles.toggleLabel}>AI Access</span>
              <button type="button"
                className={`${styles.toggleBtn} ${aiStatus.aiEnabled === "enabled" ? styles.toggleOn : styles.toggleOff}`}
                disabled={submitting} onClick={toggleAi}>
                {aiStatus.aiEnabled === "enabled" ? "On" : "Off"}
              </button>
            </div>
          </section>

          {isLitellmMode && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>API Keys</h2>
              <ProviderKeyList
                providers={aiStatus.providers ?? []}
                submitting={submitting}
                onAdd={addKey}
                onRemove={(provider) => setConfirmRemove(provider)}
              />
            </section>
          )}

          {/* Per-provider key hints do not apply to a subscription, and would
              contradict the section above that says no key is required. */}
          {aiStatus.aiMode !== "chatgpt" && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Capabilities</h2>
              <CapabilityList
                capabilities={aiStatus.capabilities ?? []}
                configuredProviders={new Set(configuredKeys.map((k) => k.provider))}
              />
            </section>
          )}
        </>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {confirmRemove && (
        <RemoveKeyDialog
          provider={confirmRemove}
          configuredKeys={configuredKeys}
          ztcManaged={aiStatus.ztcManaged ?? false}
          submitting={submitting}
          onConfirm={async () => { await removeKey(confirmRemove); setConfirmRemove(null); }}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
};

export const AiModels = (): JSX.Element => {
  const navigate = useNavigate();
  const { claws, status: clawsStatus } = useClaws();
  const [selectedClaw, setSelectedClaw] = useState<OpenClaw | null>(null);

  useEffect(() => {
    if (claws.length > 0 && !selectedClaw) setSelectedClaw(claws[0] as OpenClaw);
  }, [claws, selectedClaw]);

  if (clawsStatus === "loading") {
    return (
      <div className={styles.page}>
        <AiStatusSkeleton />
      </div>
    );
  }

  if (claws.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.emptyShell}>
          <section className={styles.emptyCard} aria-labelledby="ai-models-empty-title">
            <span className={styles.emptyEyebrow}>// AI MODELS</span>
            <h1 id="ai-models-empty-title" className={styles.emptyTitle}>
              Spin up an instance first
            </h1>
            <p className={styles.emptyBody}>
              Provider keys, modality coverage, and spend live alongside each
              OpenClaw. Launch one and it'll show up here for configuration.
            </p>
            <button
              type="button"
              className={styles.emptyCta}
              onClick={() => navigate("/dashboard/onboarding")}
            >
              Start onboarding
              <span aria-hidden="true">→</span>
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <AdminPageIntro
        title="AI models"
        aside={
          claws.length > 1 ? (
            <select className={styles.clawSelect} value={selectedClaw?.tenantId ?? ""}
              onChange={(e) => { const c = claws.find((cl) => cl.tenantId === e.target.value); if (c) setSelectedClaw(c as OpenClaw); }}>
              {claws.map((c) => <option key={c.tenantId} value={c.tenantId}>{c.name}</option>)}
            </select>
          ) : undefined
        }
      >
        Per-instance provider keys, modality coverage and the AI on/off toggle.
      </AdminPageIntro>

      {selectedClaw !== null ? (
        <AiModelsBody key={selectedClaw.tenantId} claw={selectedClaw} />
      ) : (
        <AiStatusSkeleton />
      )}
    </div>
  );
};
